import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { InvoiceModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/invoice/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/invoice/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/invoice/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/invoice/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/invoice/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/invoice/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/invoice/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/invoice/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/invoice/DELETE_FAILURE'

const UPDATE_REQUEST = 'realbase/invoice/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/invoice/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/invoice/UPDATE_FAILURE'

export type InvoiceModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: InvoiceModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadInvoices(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().invoices.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/invoices.json', options)
      .then(({ data }: { data: InvoiceModel[] }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadInvoice(invoiceId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.invoices.loadedForKeys]
    const loadedIds = [...state.invoices.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (invoiceId && !loadedIds.includes(invoiceId)){
      loadedIds.push(invoiceId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/invoices/${invoiceId}.json`, options)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createInvoice(invoice: Partial<InvoiceModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      invoice,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/invoices.json', options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteInvoice(invoice: DeleteParams<InvoiceModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    const { id } = invoice
    dispatch(deleteRequest())

    const promise = api(`/invoices/${id}.json`, {}, config)
      .then(({ data }: { data: { id: number} }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

interface ImportExternalInvoiceParams {
  agency_id?: number,
  campaign_id: number,
  external_invoice_id: string,
}

export function importExternalInvoice(
  importExternalInvoiceParams: ImportExternalInvoiceParams,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify(importExternalInvoiceParams),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/invoices/import_external_invoice.json', options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignInvoice(
  params: {
    campaignItemIds?: number[],
    invoice: Partial<InvoiceModel>,
    orderIds?: number[],
  },
  options: EntityOptions = {},
) {
  const { campaignItemIds = [], invoice, orderIds = [] } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_item_ids: campaignItemIds,
      invoice,
      order_ids: orderIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/invoices/create_campaign_invoice.json', options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function approveInvoice(
  invoice: UpdateParams<InvoiceModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/invoices/${invoice.id}/approve.json`, options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createExternalInvoice(
  invoice: UpdateParams<InvoiceModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/invoices/${invoice.id}/create_external_invoice.json`, options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function syncExternalInvoices(
  syncParams: {
    agencyId: number,
    forceSync?: boolean,
  },
  options: EntityOptions = {},
) {
  const { agencyId, forceSync } = syncParams

  const config = {
    method: 'PUT',
    data: JSON.stringify({
      agency_id: agencyId,
      force_sync: forceSync,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/invoices/sync_external_invoices.json', options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function syncExternalInvoice(
  invoice: UpdateParams<InvoiceModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/invoices/${invoice.id}/sync_external_invoice.json`, options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateInvoice(
  invoice: UpdateParams<InvoiceModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      invoice,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/invoices/${invoice.id}.json`, options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateInvoiceTotal(invoiceId: number, total: number) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { invoices } = state.entities

    const invoice = invoices[invoiceId]

    if (invoice){
      const updatedInvoice = { ...invoice, total }
      const normalizedJson = normalize(updatedInvoice, Schemas.INVOICE)

      dispatch(updateEntities(normalizedJson))
      dispatch(updateSuccess())
    } else {
      dispatch(updateFailure(['Invoice does not exist.']))
    }
  }
}

export function voidInvoice(
  invoice: UpdateParams<InvoiceModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/invoices/${invoice.id}/void.json`, options, config)
      .then(({ data }: { data: InvoiceModel }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: InvoiceModuleState = initialState,
  action: Action = { type: '' },
): InvoiceModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
